import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Briefcase, Users, AlertTriangle, CheckCircle2, BarChart3, Phone } from "lucide-react";
import { ALL_STATUSES } from "@/components/ClaimTimeline";

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [claims, setClaims] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const [o, c, u, l] = await Promise.all([
      api.get("/admin/overview").then((r) => r.data),
      api.get("/claims").then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
      api.get("/admin/leads").then((r) => r.data).catch(() => []),
    ]);
    setOverview(o); setClaims(c); setUsers(u); setLeads(l);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const updateStatus = async (claimId, status) => {
    try {
      await api.patch(`/claims/${claimId}/status`, { status, note: "Updated by admin" });
      load();
    } catch (e) {
      alert("Failed to update");
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await api.post(`/admin/users/${userId}/role`, null, { params: { role } });
      load();
    } catch (e) {
      alert("Failed to update role");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-900 text-white py-10" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF7A00] mb-2">Admin Panel</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Operations control room</h1>
          <p className="text-slate-400 mt-1">Monitor claims, manage users and keep the engine running.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 overflow-x-auto">
          {[
            { k: "overview", label: "Overview", icon: BarChart3 },
            { k: "claims", label: "Claims", icon: Briefcase },
            { k: "users", label: "Users", icon: Users },
            { k: "leads", label: "Leads", icon: Phone },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${tab === k ? "border-[#FF7A00] text-white" : "border-transparent text-slate-400 hover:text-white"}`}
              data-testid={`admin-tab-${k}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && overview && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <AdminStat icon={Briefcase} color="#003DA5" label="Total Claims" value={overview.total_claims} />
              <AdminStat icon={AlertTriangle} color="#FF7A00" label="Open Claims" value={overview.open_claims} />
              <AdminStat icon={CheckCircle2} color="#00A86B" label="Closed Claims" value={overview.closed_claims} />
              <AdminStat icon={Users} color="#3B82F6" label="Users" value={overview.total_users} />
            </div>
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
              <h2 className="font-heading font-bold text-lg mb-4">Claim status distribution</h2>
              <div className="space-y-2">
                {ALL_STATUSES.map((status) => {
                  const item = overview.distribution.find((d) => d.status === status);
                  const count = item?.count || 0;
                  const max = Math.max(1, ...overview.distribution.map((d) => d.count));
                  const pct = (count / max) * 100;
                  return (
                    <div key={status} className="flex items-center gap-3" data-testid={`dist-${status.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className="w-44 text-sm text-slate-300 shrink-0">{status}</div>
                      <div className="flex-1 h-3 rounded-full bg-slate-700 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#003DA5] to-[#FF7A00]" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-12 text-right text-sm font-mono font-bold">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "claims" && (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden" data-testid="admin-claims-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Claim ID</th>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Vehicle</th>
                  <th className="px-4 py-3 text-left">Insurer</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {claims.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No claims yet</td></tr>
                ) : claims.map((c) => (
                  <tr key={c.claim_id} className="hover:bg-slate-900/40" data-testid={`admin-claim-row-${c.claim_id}`}>
                    <td className="px-4 py-3 font-mono font-bold">{c.claim_id}</td>
                    <td className="px-4 py-3">{c.customer?.name}<div className="text-xs text-slate-400">{c.customer?.mobile}</div></td>
                    <td className="px-4 py-3">{c.vehicle?.make} {c.vehicle?.model}<div className="text-xs text-slate-400">{c.vehicle?.reg_no}</div></td>
                    <td className="px-4 py-3">{c.insurance?.insurer}</td>
                    <td className="px-4 py-3">
                      <select
                        value={c.current_status}
                        onChange={(e) => updateStatus(c.claim_id, e.target.value)}
                        className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-xs"
                        data-testid={`admin-status-select-${c.claim_id}`}
                      >
                        {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "users" && (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((u) => (
                  <tr key={u.user_id} className="hover:bg-slate-900/40" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3 text-slate-300">{u.email}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role || "customer"}
                        onChange={(e) => updateRole(u.user_id, e.target.value)}
                        className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-xs"
                        data-testid={`admin-role-select-${u.user_id}`}
                      >
                        {["customer", "admin", "partner", "surveyor", "workshop"].map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leads" && (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Lead ID</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Mobile</th>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-left">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {leads.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No leads yet</td></tr>
                ) : leads.map((l) => (
                  <tr key={l.lead_id} className="hover:bg-slate-900/40" data-testid={`admin-lead-row-${l.lead_id}`}>
                    <td className="px-4 py-3 font-mono font-bold">{l.lead_id}</td>
                    <td className="px-4 py-3">{l.name || "—"}</td>
                    <td className="px-4 py-3 font-mono">{l.mobile}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{l.source}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminStat({ icon: Icon, color, label, value }) {
  return (
    <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}30`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-heading font-extrabold text-3xl mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
